import { EmailTemplateDto } from "@babackapp/private-api-client/types";
import { NotificationTemplatePreview } from "@babackapp/templates-editor/notification-template-preview";
import { TrackingTemplatesRoutingProvider } from "@babackapp/templates-editor/tracking-routing-provider";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@babackapp/ui/components/sheet";
import { useTranslation } from "react-i18next";
import { Suspense, useMemo } from "react";
import { type EmailOverrides, applyEmailOverrides } from "./apply-email-overrides";

interface EmailPreviewSheetProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  template: EmailTemplateDto;
  overrides?: EmailOverrides | null;
}

export function EmailPreviewSheet({
  isOpen,
  onOpenChange,
  template,
  overrides,
}: EmailPreviewSheetProps) {
  const { t } = useTranslation("trackoffice");

  const mergedTemplate = useMemo(() => {
    if (!overrides) return template;

    const { content, subject } = applyEmailOverrides(
      template.content,
      (template.subject ?? {}) as Record<string, string>,
      overrides,
    );

    return { ...template, content, subject };
  }, [template, overrides]);

  return (
    <Sheet open={isOpen} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-[50vw] p-0 flex flex-col"
      >
        <SheetHeader className="px-6 py-4 border-b shrink-0">
          <SheetTitle>
            {t(
              "workflowsEditor.emailPreview.title",
              "Email preview",
            )}
          </SheetTitle>
          <SheetDescription>
            {t(
              "workflowsEditor.emailPreview.description",
              "The actual email rendering may vary depending on the order information.",
            )}
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-hidden">
          <Suspense
            fallback={
              <div className="flex items-center justify-center h-full text-muted-foreground">
                {t("common.loading", "Loading...")}
              </div>
            }
          >
            <TrackingTemplatesRoutingProvider>
              <NotificationTemplatePreview template={mergedTemplate} />
            </TrackingTemplatesRoutingProvider>
          </Suspense>
        </div>
      </SheetContent>
    </Sheet>
  );
}
